<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import OnboardingAPI from 'dashboard/api/onboarding';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { resolveTargetInbox } from './resolveTargetInbox';

const props = defineProps({
  progress: { type: Object, required: true },
});
const emit = defineEmits(['refresh', 'update:can-advance']);

const { t } = useI18n();

const isAiAvailable = computed(
  () => props.progress.capabilities?.ai_agent_available
);
const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(6)
);

const inboxId = ref(null);
const selectedRule = ref(null);
const saveError = ref(null);

// With Captain available there are two mutually-exclusive options, so picking one is
// required to advance. Without it, "return to the same agent" is the only option and
// it's opt-in — leaving it unchecked is a valid choice (no automatic return rule), not
// an incomplete step.
const isValid = computed(() => !isAiAvailable.value || !!selectedRule.value);

watch(isValid, value => emit('update:can-advance', value), { immediate: true });

const markStepCompleted = async () => {
  try {
    await OnboardingAPI.update({
      completed_steps: [...new Set([...props.progress.completed_steps, 6])],
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const persistRule = async () => {
  if (!inboxId.value) return;
  try {
    await InboxesAPI.update(inboxId.value, {
      lead_return_rule: selectedRule.value,
    });
    saveError.value = null;
    if (isValid.value && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    saveError.value =
      error?.response?.data?.error || t('ONBOARDING.STEP_6.UPDATE_ERROR');
  }
};

const selectRule = value => {
  selectedRule.value = value;
  persistRule();
};

// "same_agent" is the only option without Captain available — a lone radio
// can't be unselected, so it becomes a checkbox instead.
const toggleSameAgent = checked => {
  selectRule(checked ? 'same_agent' : null);
};

// Re-syncs when the rule is changed from another screen so both views stay live.
const refreshFromServer = async () => {
  try {
    const { data } = await InboxesAPI.get();
    const inbox = resolveTargetInbox(data?.payload || [], props.progress);
    if (!inbox) return;

    inboxId.value = inbox.id;
    selectedRule.value = inbox.lead_return_rule || null;
  } catch (error) {
    // Ignore — a background refresh failing shouldn't disrupt an in-progress edit
  }
};

onMounted(async () => {
  try {
    const { data } = await InboxesAPI.get();
    const inbox = resolveTargetInbox(data?.payload || [], props.progress);
    if (inbox) {
      inboxId.value = inbox.id;
      selectedRule.value = inbox.lead_return_rule || null;
    }
    if (isValid.value && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
  emitter.on(BUS_EVENTS.INBOX_CHANGED, refreshFromServer);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.INBOX_CHANGED, refreshFromServer);
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start w-full h-full p-8 md:px-16"
  >
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_6.TITLE') }}
        </h1>
        <span
          class="px-2 py-0.5 text-xs font-semibold rounded-full bg-n-brand/10 text-n-brand"
        >
          {{ t('ONBOARDING.STEP_6.AI_BADGE') }}
        </span>
        <a
          href="https://help.leadster.com.br/article/418-retorno-de-usuarios-leads-o-que-acontece-quando-o-lead-volta"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_6.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">{{ t('ONBOARDING.STEP_6.SUBTITLE') }}</p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_6.DESCRIPTION') }}</p>

      <div
        :role="isAiAvailable ? 'radiogroup' : null"
        class="flex flex-col w-full max-w-2xl gap-3"
      >
        <label
          v-if="isAiAvailable"
          class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-n-weak"
          :class="
            selectedRule === 'always_ai' ? 'border-n-brand bg-n-brand/5' : ''
          "
        >
          <input
            type="radio"
            name="lead-return-rule"
            value="always_ai"
            :checked="selectedRule === 'always_ai'"
            class="mt-1 h-4 w-4 shrink-0 accent-n-brand"
            @change="selectRule('always_ai')"
          />
          <span class="flex flex-col gap-1">
            <span class="font-medium text-n-slate-12">
              {{ t('ONBOARDING.STEP_6.ALWAYS_AI_TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('ONBOARDING.STEP_6.ALWAYS_AI_DESCRIPTION') }}
            </span>
          </span>
        </label>

        <label
          v-if="isAiAvailable"
          class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-n-weak"
          :class="
            selectedRule === 'same_agent' ? 'border-n-brand bg-n-brand/5' : ''
          "
        >
          <input
            type="radio"
            name="lead-return-rule"
            value="same_agent"
            :checked="selectedRule === 'same_agent'"
            class="mt-1 h-4 w-4 shrink-0 accent-n-brand"
            @change="selectRule('same_agent')"
          />
          <span class="flex flex-col gap-1">
            <span class="font-medium text-n-slate-12">
              {{ t('ONBOARDING.STEP_6.SAME_AGENT_TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('ONBOARDING.STEP_6.SAME_AGENT_DESCRIPTION') }}
            </span>
          </span>
        </label>

        <!-- Without Captain available this is the only option, so it's a
           checkbox (can be unmarked) rather than a lone, always-on radio. -->
        <label
          v-else
          class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-n-weak"
          :class="
            selectedRule === 'same_agent' ? 'border-n-brand bg-n-brand/5' : ''
          "
        >
          <Checkbox
            :model-value="selectedRule === 'same_agent'"
            class="mt-1"
            @update:model-value="toggleSameAgent"
          />
          <span class="flex flex-col gap-1">
            <span class="font-medium text-n-slate-12">
              {{ t('ONBOARDING.STEP_6.SAME_AGENT_TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('ONBOARDING.STEP_6.SAME_AGENT_DESCRIPTION') }}
            </span>
          </span>
        </label>

        <p v-if="saveError" class="text-sm text-n-ruby-9">{{ saveError }}</p>
      </div>
    </div>
  </div>
</template>
